import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  PrioriteBadge, StatutBadge, EnRetardBadge, STATUT_RECLAMATION, PRIORITES, CATEGORIES_REC, CANAUX, SLA_HOURS, LABELS_MAP,
} from "@/components/StatusBadge";
import { Plus, Search, Sparkles, MessageSquareWarning, Zap, FileDown, FileSpreadsheet, FileType2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DiscussionThread from "@/components/DiscussionThread";

const KANBAN_COLS = [
  { key: "nouvelle", label: "Nouvelle" },
  { key: "assignee", label: "Assignée" },
  { key: "en_traitement", label: "En traitement" },
  { key: "en_attente_client", label: "En attente client" },
  { key: "validee", label: "Validée" },
  { key: "resolue", label: "Résolue" },
];

function fmtEcheance(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function priorityHint(p) {
  const h = SLA_HOURS[p];
  if (!h) return "";
  if (h < 24) return `SLA ${h}h`;
  return `SLA ${Math.round(h / 24)}j`;
}

export default function Reclamations() {
  const { user } = useAuth();
  const isWriter = user?.role !== "auditeur";
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterPrio, setFilterPrio] = useState("all");
  const [filterRetard, setFilterRetard] = useState("all");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    objet: "", description: "", categorie: "autre", canal: "telephone", priorite: "normale", client_id: "", service: "",
  });

  const load = async () => {
    const params = { limit: 300 };
    if (filterStatut !== "all") params.statut = filterStatut;
    if (filterPrio !== "all") params.priorite = filterPrio;
    if (filterRetard === "retard") params.en_retard = true;
    if (filterRetard === "ok") params.en_retard = false;
    if (q) params.q = q;
    const { data } = await api.get("/reclamations", { params });
    setRows(data);
  };

  useEffect(() => { load(); }, [filterStatut, filterPrio, filterRetard]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => { /* ignore */ });
    api.get("/users").then((r) => setUsers(r.data)).catch(() => { /* ignore */ });
  }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.client_id) delete payload.client_id;
      const { data } = await api.post("/reclamations", payload);
      toast.success(`Réclamation créée (${data.numero})`);
      setOpen(false);
      setForm({ objet: "", description: "", categorie: "autre", canal: "telephone", priorite: "normale", client_id: "", service: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const updateField = async (id, changes) => {
    try {
      const { data } = await api.patch(`/reclamations/${id}`, changes);
      load();
      if (detail?.id === id) setDetail(data);
      toast.success("Mise à jour effectuée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const runAi = async () => {
    if (!detail) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/ai/analyze-reclamation/${detail.id}`);
      setDetail({ ...detail, ai_resume: data.analysis });
      toast.success("Analyse IA terminée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur IA");
    } finally {
      setAiLoading(false);
    }
  };

  const autoAssign = async () => {
    if (!detail) return;
    setAssigning(true);
    try {
      const { data } = await api.post(`/reclamations/${detail.id}/auto-assign`);
      setDetail(data.reclamation);
      toast.success(`Affecté à ${data.assignee.name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setAssigning(false);
    }
  };

  const downloadExport = async (fmt) => {
    try {
      const params = new URLSearchParams();
      if (filterStatut !== "all") params.set("statut", filterStatut);
      if (filterPrio !== "all") params.set("priorite", filterPrio);
      const url = `${API_BASE}/export/reclamations.${fmt}?${params.toString()}`;
      const res = await api.get(`/export/reclamations.${fmt}?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `reclamations.${fmt}`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error("Erreur export");
    }
  };

  const displayed = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) =>
      r.objet?.toLowerCase().includes(s) || r.numero?.toLowerCase().includes(s) || r.description?.toLowerCase().includes(s)
    );
  }, [rows, q]);

  const groupedKanban = useMemo(() => {
    const groups = Object.fromEntries(KANBAN_COLS.map((c) => [c.key, []]));
    displayed.forEach((r) => {
      if (groups[r.statut]) groups[r.statut].push(r);
    });
    return groups;
  }, [displayed]);

  return (
    <div>
      <PageHeader
        eyebrow="Module"
        title="Réclamations"
        description="SLA critique 4h · haute 24h · normale 72h · faible 7j — priorisation, affectation, IA d'analyse."
        testid="reclamations-header"
        actions={
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="rec-export-btn">
                  <FileDown className="w-4 h-4 mr-2" /> Exporter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => downloadExport("xlsx")} data-testid="rec-export-xlsx">
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadExport("pdf")} data-testid="rec-export-pdf">
                  <FileType2 className="w-4 h-4 mr-2" /> PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {isWriter && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="reclamation-create-btn" className="bg-blue-900 hover:bg-blue-800 rounded-full">
                    <Plus className="w-4 h-4 mr-2" /> Nouvelle réclamation
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-display">Créer une réclamation</DialogTitle>
                    <DialogDescription>Le numéro et l&apos;échéance sont attribués automatiquement.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={submitCreate} className="space-y-3">
                    <div className="space-y-1.5">
                      <Label>Objet</Label>
                      <Input required value={form.objet} onChange={(e) => setForm({ ...form, objet: e.target.value })} data-testid="rec-objet-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Description</Label>
                      <Textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="rec-description-input" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label>Catégorie</Label>
                        <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                          <SelectTrigger data-testid="rec-categorie-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{CATEGORIES_REC.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Canal</Label>
                        <Select value={form.canal} onValueChange={(v) => setForm({ ...form, canal: v })}>
                          <SelectTrigger data-testid="rec-canal-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{CANAUX.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Priorité</Label>
                        <Select value={form.priorite} onValueChange={(v) => setForm({ ...form, priorite: v })}>
                          <SelectTrigger data-testid="rec-priorite-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{PRIORITES.map((p) => <SelectItem key={p} value={p}>{LABELS_MAP[p]} — {priorityHint(p)}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Client (optionnel)</Label>
                      <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="rec-client-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun</SelectItem>
                          {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom} {c.prenom || ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Service</Label>
                      <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="Ex: Réclamations, Facturation..." />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                      <Button type="submit" disabled={saving} data-testid="rec-submit-btn">Créer</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        }
      />

      <Card className="border border-slate-200 bg-white mb-4">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                className="pl-9"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                data-testid="rec-search-input"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={filterStatut} onValueChange={setFilterStatut}>
                <SelectTrigger className="w-44" data-testid="rec-statut-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {STATUT_RECLAMATION.map((s) => <SelectItem key={s} value={s}>{LABELS_MAP[s]}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterPrio} onValueChange={setFilterPrio}>
                <SelectTrigger className="w-40" data-testid="rec-prio-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes priorités</SelectItem>
                  {PRIORITES.map((p) => <SelectItem key={p} value={p}>{LABELS_MAP[p]}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterRetard} onValueChange={setFilterRetard}>
                <SelectTrigger className="w-36" data-testid="rec-retard-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">SLA (toutes)</SelectItem>
                  <SelectItem value="retard">En retard</SelectItem>
                  <SelectItem value="ok">Dans les délais</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="liste">
        <TabsList data-testid="rec-view-tabs">
          <TabsTrigger value="liste" data-testid="rec-tab-liste">Liste</TabsTrigger>
          <TabsTrigger value="kanban" data-testid="rec-tab-kanban">Kanban</TabsTrigger>
        </TabsList>

        <TabsContent value="liste" className="mt-4">
          <Card className="border border-slate-200 bg-white">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Numéro</TableHead>
                      <TableHead>Objet</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Priorité</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>SLA</TableHead>
                      <TableHead>Échéance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayed.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">
                        <MessageSquareWarning className="w-8 h-8 mx-auto mb-2 opacity-40" /> Aucune réclamation
                      </TableCell></TableRow>
                    ) : displayed.map((r) => (
                      <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetail(r)} data-testid={`rec-row-${r.numero}`}>
                        <TableCell className="font-mono text-xs text-slate-700">{r.numero}</TableCell>
                        <TableCell className="font-medium text-slate-900 max-w-xs truncate">{r.objet}</TableCell>
                        <TableCell className="text-slate-600 text-sm capitalize">{r.categorie}</TableCell>
                        <TableCell><PrioriteBadge value={r.priorite} /></TableCell>
                        <TableCell><StatutBadge value={r.statut} /></TableCell>
                        <TableCell>{r.en_retard ? <EnRetardBadge /> : <span className="text-slate-400 text-xs">OK</span>}</TableCell>
                        <TableCell className="text-xs text-slate-500 font-mono">{fmtEcheance(r.echeance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="kanban" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="rec-kanban">
            {KANBAN_COLS.map((col) => (
              <div key={col.key} className="kanban-col p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-700">{col.label}</div>
                  <span className="text-xs bg-white border border-slate-200 rounded-full px-2 py-0.5 text-slate-500">{groupedKanban[col.key].length}</span>
                </div>
                <div className="space-y-2">
                  {groupedKanban[col.key].map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setDetail(r)}
                      className="w-full text-left bg-white rounded-lg p-3 border border-slate-200 hover:border-blue-800 transition"
                      data-testid={`kanban-card-${r.numero}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-mono text-[10px] text-slate-500">{r.numero}</span>
                        <PrioriteBadge value={r.priorite} />
                      </div>
                      <div className="text-sm font-medium text-slate-900 line-clamp-2">{r.objet}</div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-xs text-slate-500 capitalize">{r.categorie}</div>
                        {r.en_retard && <EnRetardBadge />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-sm text-slate-500">{detail.numero}</span>
                  <span className="truncate">{detail.objet}</span>
                  {detail.en_retard && <EnRetardBadge />}
                </DialogTitle>
                <DialogDescription>
                  {detail.categorie} · {detail.canal?.replace("_", " ")} · créée le {new Date(detail.created_at).toLocaleString("fr-FR")} · échéance {fmtEcheance(detail.echeance)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Statut</Label>
                    <Select value={detail.statut} onValueChange={(v) => updateField(detail.id, { statut: v })} disabled={!isWriter}>
                      <SelectTrigger data-testid="rec-detail-statut"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUT_RECLAMATION.map((s) => <SelectItem key={s} value={s}>{LABELS_MAP[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Priorité</Label>
                    <Select value={detail.priorite} onValueChange={(v) => updateField(detail.id, { priorite: v })} disabled={!isWriter}>
                      <SelectTrigger data-testid="rec-detail-prio"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITES.map((p) => <SelectItem key={p} value={p}>{LABELS_MAP[p]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Affectée à</Label>
                    <Select value={detail.assigne_a || "none"} onValueChange={(v) => updateField(detail.id, { assigne_a: v === "none" ? null : v })} disabled={!isWriter}>
                      <SelectTrigger data-testid="rec-detail-assign"><SelectValue placeholder="Non affectée" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non affectée</SelectItem>
                        {users.filter((u) => u.role !== "auditeur").map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {isWriter && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={autoAssign} disabled={assigning} data-testid="rec-autoassign-btn">
                      <Zap className="w-4 h-4 mr-1.5 text-blue-800" /> {assigning ? "Affectation..." : "Affectation auto"}
                    </Button>
                    <span className="text-xs text-slate-500">— attribue à l&apos;agent du service avec le moins de dossiers actifs</span>
                  </div>
                )}

                <div>
                  <Label className="text-xs text-slate-500">Objet</Label>
                  <Input
                    defaultValue={detail.objet || ""}
                    disabled={!isWriter}
                    onBlur={(e) => e.target.value !== (detail.objet || "") && updateField(detail.id, { objet: e.target.value })}
                    data-testid="rec-detail-objet"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Catégorie</Label>
                    <Select value={detail.categorie} onValueChange={(v) => updateField(detail.id, { categorie: v })} disabled={!isWriter}>
                      <SelectTrigger data-testid="rec-detail-categorie"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES_REC.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Canal</Label>
                    <Select value={detail.canal} onValueChange={(v) => updateField(detail.id, { canal: v })} disabled={!isWriter}>
                      <SelectTrigger data-testid="rec-detail-canal"><SelectValue /></SelectTrigger>
                      <SelectContent>{CANAUX.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Description</Label>
                  <Textarea
                    rows={4}
                    defaultValue={detail.description || ""}
                    disabled={!isWriter}
                    onBlur={(e) => e.target.value !== (detail.description || "") && updateField(detail.id, { description: e.target.value })}
                    data-testid="rec-detail-description"
                    className="mt-1 bg-slate-50/60"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-500 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-violet-600" /> Analyse IA (Claude)</Label>
                    {isWriter && (
                      <Button variant="outline" size="sm" onClick={runAi} disabled={aiLoading} data-testid="rec-ai-btn">
                        {aiLoading ? "Analyse..." : "Lancer l'analyse"}
                      </Button>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap border border-violet-200 rounded-lg p-3 bg-violet-50/40 min-h-[80px]">
                    {detail.ai_resume || <span className="text-slate-400">Aucune analyse pour le moment.</span>}
                  </div>
                </div>

                {isWriter && (
                  <div>
                    <Label className="text-xs text-slate-500">Réponse au client</Label>
                    <Textarea
                      rows={3}
                      defaultValue={detail.reponse || ""}
                      onBlur={(e) => e.target.value !== (detail.reponse || "") && updateField(detail.id, { reponse: e.target.value })}
                      data-testid="rec-detail-reponse"
                    />
                  </div>
                )}

                <DiscussionThread entityType="reclamation" entityId={detail.id} title="Discussion sur cette réclamation" />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
